import React from 'react';
import { Trash2 } from 'lucide-react';
import { initials } from '../../lib/format.js';
import { depthLabel, expiryText, seatBadge } from '../../lib/teamHierarchy.js';

/**
 * "My Agents" roster: name, Team Admin switch, subscription, depth, remove.
 * @param {{items: Array<object>, onToggleAdmin: (item: object, next: boolean) => void, onRemove: (item: object) => void}} props
 * @returns {JSX.Element}
 */
export default function AgentRoster({ items, onToggleAdmin, onToggleOwnTeam = () => {}, onRemove }) {
  return (
    <div className="overflow-x-auto" data-testid="agent-roster">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-white/50 text-xs border-b border-civix-border/60">
            <th className="py-2 pr-4 font-medium">My agents</th>
            <th className="py-2 pr-4 font-medium">Team admin</th>
            <th className="py-2 pr-4 font-medium">Can add own team</th>
            <th className="py-2 pr-4 font-medium">Subscription</th>
            <th className="py-2 pr-4 font-medium">Level</th>
            <th className="py-2 w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-civix-border/40">
          {items.length === 0 && (
            <tr><td colSpan={6} className="py-8 text-center text-xs text-white/40" data-testid="roster-empty">No team members yet — use “Add Team Member” to invite your first agent.</td></tr>
          )}
          {items.map((a) => {
            const badge = seatBadge(a);
            return (
              <tr key={a._id} data-testid="roster-row" className={a.active ? '' : 'opacity-50'}>
                <td className="py-3 pr-4">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full overflow-hidden bg-civix-gradient text-white text-xs font-bold flex items-center justify-center shrink-0">
                      {a.avatar ? <img src={a.avatar} alt="" className="w-full h-full object-cover" /> : initials(a.name)}
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{a.name}</div>
                      <div className="text-xs text-white/50 truncate">{a.email}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={a.isTeamAdmin}
                    aria-label={`Team admin for ${a.name}`}
                    data-testid={`team-admin-${a._id}`}
                    disabled={!a.active}
                    onClick={() => onToggleAdmin(a, !a.isTeamAdmin)}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed ${a.isTeamAdmin ? 'bg-civix-purple' : 'bg-slate-400'}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${a.isTeamAdmin ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="py-3 pr-4">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!a.canInviteOwnTeam}
                    aria-label={`Can add own team for ${a.name}`}
                    data-testid={`own-team-${a._id}`}
                    disabled={!a.active}
                    onClick={() => onToggleOwnTeam(a, !a.canInviteOwnTeam)}
                    className={`relative inline-flex h-5 w-9 rounded-full border-2 border-transparent transition-colors disabled:cursor-not-allowed ${a.canInviteOwnTeam ? 'bg-civix-purple' : 'bg-slate-400'}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${a.canInviteOwnTeam ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </td>
                <td className="py-3 pr-4">
                  <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${badge.cls}`}>{badge.label}</span>
                  <div className="text-xs text-white/55 mt-1">{expiryText(a)}</div>
                </td>
                <td className="py-3 pr-4 whitespace-nowrap">
                  <span className="px-2 py-0.5 rounded-full bg-civix-purple/15 text-civix-purpleSoft text-[11px] font-semibold">{depthLabel(a)}</span>
                </td>
                <td className="py-3 text-right">
                  <button type="button" onClick={() => onRemove(a)} disabled={!a.active} aria-label={`Remove ${a.name}`} className="text-red-400 hover:text-red-300 disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
